import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setToken, getToken, apiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const login = async (identifier, password) => {
    const { data } = await api.post("/auth/login", { identifier, password });
    if (data.requires_2fa) return { requires_2fa: true, pre_auth_token: data.pre_auth_token };
    setToken(data.token);
    setUser(data.user);
    return { user: data.user, must_reset_password: data.must_reset_password };
  };

  const verify2fa = async (pre_auth_token, code) => {
    const { data } = await api.post("/auth/2fa/verify", { pre_auth_token, code });
    setToken(data.token);
    setUser(data.user);
    return { user: data.user, must_reset_password: data.must_reset_password };
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setToken(null);
    setUser(null);
  };

  const refreshUser = loadMe;

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, verify2fa, logout, refreshUser, apiError }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
